import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  MenuItem,
  MenuProps,
  Select,
  useTheme,
} from '@material-ui/core';
import { FilterOperator } from '@nemuru-eng/js-api-client';
import React, { useState } from 'react';

import { Font } from '@/components/shared/components/Font';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';
import { CustomPalette } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';

interface useMultiSelectFiltersProps {
  items?: string[];
  label?: JSX.Element;
  hideLabel?: boolean;
  field?: string;
  checkBoxRender?: (value) => JSX.Element;
  initialItems?: string[];
  defaultItems?: string[];
  defaultLabel?: JSX.Element;
  filtersValueHandler?: (filters: string[]) => string[];
}

export const useMultiSelectFilters = ({
  label,
  hideLabel = false,
  field,
  checkBoxRender = undefined,
  items,
  initialItems,
  defaultItems,
  defaultLabel,
  filtersValueHandler = (filters) => filters,
}: useMultiSelectFiltersProps) => {
  const {
    state: { dictionaryValues },
  } = useConfigContext();

  if (!field || !items || !defaultItems) {
    return {
      multiSelectFilters: [],
      multiSelectFiltersContent: <></>,
      isDefaultMultiSelectValue: true,
      resetMultiSelectToDefault: () => {},
    };
  }

  const [selectedItems, setSelectedItems] = useState(initialItems || defaultItems);
  const MenuProps = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
    PaperProps: {
      style: {
        width: 250,
      },
    },
  } as Partial<MenuProps>;

  const allItemsAreSelected = selectedItems.length === items.length;

  const selectAllItems = () => {
    if (allItemsAreSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items);
    }
  };
  const SELECT_ALL = 'SELECT_ALL';

  const getInputText = (selected) => {
    let content = defaultLabel;
    if (allItemsAreSelected) content = <Text tId={'ALL'} />;
    else {
      if (!isDefaultValue() || !defaultLabel) content = selected.map((i) => dictionaryValues[i] || i).join(', ');
    }

    return (
      <StyledTooltip
        title={selectedItems.map((i) => dictionaryValues[i] || i).join(', ')}
        tooltipProps={{ placement: 'right-end', enterDelay: 500 }}
      >
        <InputLabel style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{content}</InputLabel>
      </StyledTooltip>
    );
  };

  const { palette, shape }: { palette: CustomPalette; shape } = useTheme();

  const defaultWrapperStyle = {
    borderRadius: shape.borderRadius,
    padding: '10px 0 2px 4px',
    marginTop: '-8px',
  } as React.CSSProperties;
  const activeWrapperStyle = { ...defaultWrapperStyle, background: palette.grey[100] } as React.CSSProperties;

  const resetMultiSelectToDefault = () => {
    setSelectedItems(defaultItems);
  };

  const isDefaultValue = () => {
    if (selectedItems.length !== defaultItems.length) return false;
    return selectedItems.every((element) => {
      if (defaultItems.includes(element)) {
        return true;
      }
      return false;
    });
  };

  const statusSelector = (
    <Box style={isDefaultValue() ? defaultWrapperStyle : activeWrapperStyle}>
      {!isDefaultValue() && (
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={() => resetMultiSelectToDefault()}
          style={{
            float: 'right',
            margin: '-5px -5px -25px',
            zIndex: 1,
          }}
        >
          <i className="material-icons-outlined" style={{ verticalAlign: 'text-top', fontSize: '24px' }}>
            close
          </i>
        </IconButton>
      )}
      <FormControl>
        {!hideLabel && label && <InputLabel style={{ marginTop: '-8px' }}>{label}</InputLabel>}
        <Select
          style={{ width: '250px', height: '28px' }}
          multiple={true}
          value={selectedItems}
          onChange={(e) => {
            const valueArray = e.target.value as [];
            if (!valueArray.find((e) => e === SELECT_ALL)) {
              setSelectedItems(valueArray);
            } else {
              selectAllItems();
            }
          }}
          input={<Input />}
          renderValue={getInputText}
          MenuProps={MenuProps}
        >
          <MenuItem value={SELECT_ALL}>
            <Box px={2} py={1} width={'100%'}>
              <Font variant={'h5'} align={'center'}>
                <Text tId={allItemsAreSelected ? 'DESELECT_ALL' : 'SELECT_ALL'} />
              </Font>
            </Box>
          </MenuItem>
          {items.map((status) => (
            <MenuItem key={status} value={status}>
              <>
                <Checkbox checked={selectedItems.indexOf(status) > -1} />
                {checkBoxRender ? checkBoxRender(status) : dictionaryValues[status] || status}
              </>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );

  const getMultiSelectFiltersValue = () => {
    const filtersValueArray = [...selectedItems];
    return filtersValueHandler(filtersValueArray);
  };

  const multiSelectFilters = [
    {
      field: field,
      operator: FilterOperator.IN,
      value: getMultiSelectFiltersValue(),
    },
  ];

  return {
    multiSelectFilters,
    multiSelectFiltersContent: statusSelector,
    isDefaultMultiSelectValue: isDefaultValue(),
    resetMultiSelectToDefault,
  };
};
